/**
 * Collapse destinations that share `url`+`streamKey` into one. Used to merge the
 * server's synthetic BLP/platform record (no `id`) with a gr_destinations row
 * that events-api auto-seeded with the same credentials. Keep the id-having row
 * (richer for the UI — editable, deletable, named); drop the id-less synthetic.
 *
 * Relies on byte-identical url+streamKey, which is currently guaranteed because
 * both sources pull credentials from the same `getSessionRtmpCredentials` path.
 * See destination.types.ts for the BLP synthetic contract.
 */
export function dedupeDestinationsByCredentials<T extends { id?: number | null; url?: string; streamKey?: string }>(
  destinations: T[]
): T[] {
  const deduped: T[] = [];
  const seenByCreds = new Map<string, number>();
  for (const dest of destinations) {
    if (!dest.url || !dest.streamKey) {
      deduped.push(dest);
      continue;
    }
    // `␟` (U+241F) is the glyph for the ASCII Unit Separator — a delimiter that
    // can't appear in a URL or stream key, so the joined hash key is collision-free.
    const credsKey = `${dest.url}␟${dest.streamKey}`;
    const existingIndex = seenByCreds.get(credsKey);
    if (existingIndex === undefined) {
      seenByCreds.set(credsKey, deduped.push(dest) - 1);
      continue;
    }
    if (deduped[existingIndex].id == null && dest.id != null) {
      deduped[existingIndex] = dest;
    }
  }
  return deduped;
}
